import { useMemo } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { ModalFormLayout } from '@/components/form/layout/ModalFormLayout';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { DOCUMENT_TYPE_OPTIONS } from '@/modules/documents/documents.constants';
import {
  DocumentSummaryPromptDocumentType,
  DocumentType,
} from '@/types/schema';

import { CreateCustomSummaryPromptFormShape } from './CreateCustomSummaryPromptModal';

// Document types that should not have independent custom summary prompts.
const EXCLUDED_DOCUMENT_TYPES_FOR_SUMMARY_PROMPTS: DocumentType[] = [
  DocumentType.TrustAmendment,
  DocumentType.TrustRelatedDocument,
  DocumentType.CertificateOfTrust,
  DocumentType.WillCodicil,
  DocumentType.OperatingAgreementAmendment,
  DocumentType.PartnershipAgreementAmendment,
  DocumentType.LlcAgreementAmendment,
];

export function CreateCustomSummaryPromptForm() {
  const { control } = useFormContext<CreateCustomSummaryPromptFormShape>();
  const documentTypeOptions: TypeaheadSelectInputOption<DocumentSummaryPromptDocumentType>[] =
    useMemo(
      () =>
        DOCUMENT_TYPE_OPTIONS.filter((docType) => !docType.disabled)
          .filter(
            (docType) =>
              !EXCLUDED_DOCUMENT_TYPES_FOR_SUMMARY_PROMPTS.includes(
                docType.value
              )
          )
          .map((docType) => ({
            value:
              docType.value as unknown as DocumentSummaryPromptDocumentType,
            display: docType.display,
          })),
      []
    );

  return (
    <ModalFormLayout>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<CreateCustomSummaryPromptFormShape>
            control={control}
            fieldName="displayName"
            label="Prompt name"
            required
            placeholder="Enter prompt name"
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTypeaheadSelectInput<CreateCustomSummaryPromptFormShape>
            control={control}
            fieldName="documentType"
            label="Document type"
            options={documentTypeOptions}
            required
            placeholder="Select document type"
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareCheckbox<CreateCustomSummaryPromptFormShape>
            control={control}
            fieldName="shouldAutoRun"
            label="Auto run"
            helpText="When enabled, this prompt will auto-run for all document uploads of the selected type"
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareCheckbox<CreateCustomSummaryPromptFormShape>
            control={control}
            fieldName="isDefaultForDocType"
            label="Default"
            helpText="When enabled, the summary will appear first and be pre-selected for new summaries"
          />
        </FormLayoutItem>
      </FormLayoutRow>
    </ModalFormLayout>
  );
}
